// Order detail page — M2 Phase 5.6 surface. Route: /orders/:id
//
// Layout mirrors QuoteDetailPage:
//   - Back link → header card (navy gradient) → tab strip → tab body →
//     bottom action row.
//
// Three tabs:
//   - Details (default) — two-column grid. Left: PO info + Ship info cards.
//     Right: status-change timeline (filtered from _audit).
//   - Parts — read-only list of the parts on the originating quote. We load
//     the source quote (order.fromQuoteId) and render its partIds via
//     PartCardImage. Orders themselves don't carry a parts array (yet) —
//     the quote is the source of truth.
//   - Activity — full _audit[] timeline (mirrors PartDetailPage's History).
//
// Bottom action row: shows only the valid status transitions per
// ORDER_STATUS_TRANSITIONS for the current status. Cancel ("→ void") uses
// a tiny inline reason modal so we can pass it through to
// transitionOrderStatus. Delete is stubbed (alert) — soft-delete is a
// future pass.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCustomer,
  getOrder,
  getPart,
  getQuote,
  transitionOrderStatus,
} from '@/shared/crm';
import { ORDER_STATUS_TRANSITIONS } from '@/shared/crmValidators';
import type {
  AuditEntry,
  Customer,
  Order,
  OrderStatus,
  Part,
  Quote,
} from '@/shared/crmTypes';
import { TierBadge } from '@/components/TierBadge';
import { PartCardImage } from '@/components/PartCardImage';

type Tab = 'details' | 'parts' | 'activity';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'details', label: 'Details' },
  { key: 'parts', label: 'Parts' },
  { key: 'activity', label: 'Activity' },
];

// Mirror the inline OrderStatusPill from CustomerDetailPage — Tailwind tints
// per status. Kept local so we don't reach across pages for a presentational
// helper.
const ORDER_STATUS_PILL: Record<OrderStatus, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-amber/15 text-amber' },
  in_production: { label: 'In production', cls: 'bg-orange/15 text-orange' },
  shipped: { label: 'Shipped', cls: 'bg-blue/15 text-blue' },
  closed: { label: 'Closed', cls: 'bg-green/15 text-green' },
  void: { label: 'Void', cls: 'bg-steel/15 text-steel' },
};

function OrderStatusPill({ status, size = 'sm' }: { status: OrderStatus; size?: 'sm' | 'md' }) {
  const meta = ORDER_STATUS_PILL[status];
  const sizeCls =
    size === 'md' ? 'text-xs px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';
  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold whitespace-nowrap ${sizeCls} ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

// Human-readable label for the bottom-row transition buttons.
function transitionLabel(to: OrderStatus): string {
  switch (to) {
    case 'in_production':
      return 'Move to production';
    case 'shipped':
      return 'Mark shipped';
    case 'closed':
      return 'Close order';
    case 'draft':
      return 'Move to draft';
    case 'void':
      return 'Cancel order';
  }
}

function fmtCurrency(n: number | undefined | null, opts?: { fractionDigits?: number }): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return '—';
  const f = opts?.fractionDigits ?? 0;
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: f,
    maximumFractionDigits: f,
  });
}

function fmtRelativeAgo(iso: string | undefined | null): string {
  if (!iso) return '—';
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  // Future dates render as "in Xd" / "in Xh"; past as "Xd ago".
  const abs = Math.abs(diffSec);
  const prefix = diffSec >= 0 ? '' : 'in ';
  const suffix = diffSec >= 0 ? ' ago' : '';
  if (abs < 60) return `${prefix}${Math.round(abs)}s${suffix}`;
  if (abs < 3600) return `${prefix}${Math.round(abs / 60)}m${suffix}`;
  if (abs < 86400) return `${prefix}${Math.round(abs / 3600)}h${suffix}`;
  return `${prefix}${Math.round(abs / 86400)}d${suffix}`;
}

function fmtDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [sourceQuote, setSourceQuote] = useState<Quote | null>(null);
  const [partsById, setPartsById] = useState<Record<string, Part>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('details');

  // Inline confirm modal for the "cancel order" transition — captures the
  // optional reason that transitionOrderStatus accepts.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [transitioning, setTransitioning] = useState<OrderStatus | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const o = await getOrder(id);
      setOrder(o);
      if (o) {
        // Load customer + source quote in parallel. Individual failures get
        // swallowed so a deleted quote doesn't blank the whole page.
        const [cust, quote] = await Promise.all([
          o.customerId ? getCustomer(o.customerId).catch(() => null) : Promise.resolve(null),
          o.fromQuoteId ? getQuote(o.fromQuoteId).catch(() => null) : Promise.resolve(null),
        ]);
        setCustomer(cust);
        setSourceQuote(quote);
        // Then parts off the quote — also tolerant of individual failures.
        if (quote?.partIds?.length) {
          const parts = await Promise.all(
            quote.partIds.map((pid) => getPart(pid).catch(() => null)),
          );
          const map: Record<string, Part> = {};
          parts.forEach((p) => {
            if (p) map[p.id] = p;
          });
          setPartsById(map);
        } else {
          setPartsById({});
        }
      }
    } catch (e) {
      setError(`Failed to load order: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse">
          Loading order…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm mb-4 flex items-center justify-between gap-3">
          <span>⚠ {error}</span>
          <button
            type="button"
            onClick={load}
            className="text-xs text-orange font-semibold hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/orders')}
          className="text-sm text-orange hover:underline"
        >
          ← Back to orders
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper border border-line rounded p-8 text-center">
          <div className="text-4xl mb-2">🔍</div>
          <div className="font-semibold text-navy mb-1">Order not found</div>
          <p className="text-sm text-text-mute mb-4">
            ID <span className="font-mono">{id}</span> doesn't exist or was soft-deleted.
          </p>
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
          >
            ← Back to orders
          </button>
        </div>
      </div>
    );
  }

  // Bottom action row — transitions filtered to forward moves only (drop the
  // self-loop the table carries so the "stay in current status" entry doesn't
  // show as a no-op button).
  const allowedTransitions = (ORDER_STATUS_TRANSITIONS[order.status] ?? []).filter(
    (s) => s !== order.status,
  );

  async function handleTransition(to: OrderStatus, reason?: string) {
    if (!order) return;
    setTransitioning(to);
    try {
      await transitionOrderStatus(order.id, to, reason);
      await load();
    } catch (e) {
      window.alert((e as Error).message);
    } finally {
      setTransitioning(null);
    }
  }

  function handleConfirmCancel() {
    const reason = cancelReason.trim();
    setCancelOpen(false);
    setCancelReason('');
    void handleTransition('void', reason || undefined);
  }

  function handleDelete() {
    // Soft-delete with audit-trail entry + undo affordance lands in a later
    // pass — stub keeps the button live so the design reads right.
    window.alert('TODO: soft-delete order (M2 Phase 5.6.x)');
  }

  return (
    <div className="max-w-6xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/orders')}
        className="text-xs text-text-mute hover:text-orange mb-2"
      >
        ← All orders
      </button>

      <OrderHeader
        order={order}
        customer={customer}
        onCustomerClick={() => customer && navigate(`/customers/${customer.id}`)}
      />

      {/* From-quote link — small card under the header */}
      {order.fromQuoteId && (
        <button
          type="button"
          onClick={() => navigate(`/quotes/${order.fromQuoteId}`)}
          className="mt-3 w-full text-left bg-paper border border-line rounded p-3 hover:border-orange hover:shadow-sm transition-all flex items-center gap-3"
        >
          <span className="text-lg" aria-hidden>
            🔗
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-text-mute uppercase tracking-wide font-semibold">
              Originated from
            </div>
            <div className="font-mono font-semibold text-navy">
              Quote {sourceQuote?.number ?? order.fromQuoteId}
            </div>
          </div>
          <span className="text-xs text-orange">Open →</span>
        </button>
      )}

      {/* Tabs */}
      <div className="border-b border-line mt-4 mb-4 flex flex-wrap">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                active
                  ? 'border-orange text-orange'
                  : 'border-transparent text-text-mute hover:text-navy'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'details' && <DetailsTab order={order} />}
      {tab === 'parts' && <PartsTab quote={sourceQuote} partsById={partsById} />}
      {tab === 'activity' && <ActivityTab order={order} />}

      {/* Bottom action row */}
      <div className="mt-6 border-t border-line pt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-text-mute">
          Last updated {fmtRelativeAgo(order.updatedAt)}.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleDelete}
            className="px-3 py-1.5 text-sm rounded border border-line-dark text-text-mute hover:bg-bg hover:text-orange"
          >
            Delete
          </button>
          <span className="text-text-mute opacity-50">|</span>
          {allowedTransitions.length === 0 ? (
            <span className="text-xs text-text-mute italic">
              No transitions — status is terminal.
            </span>
          ) : (
            allowedTransitions.map((to) => {
              const isCancel = to === 'void';
              const isPending = transitioning === to;
              return (
                <button
                  key={to}
                  type="button"
                  disabled={transitioning !== null}
                  onClick={() => {
                    if (isCancel) {
                      setCancelOpen(true);
                    } else {
                      void handleTransition(to);
                    }
                  }}
                  className={
                    isCancel
                      ? 'px-3 py-1.5 text-sm rounded border border-line-dark text-text-mute hover:bg-bg hover:text-orange disabled:opacity-40 disabled:cursor-not-allowed'
                      : 'px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed'
                  }
                  title={`Transition order to "${to}"`}
                >
                  {isPending ? '…' : transitionLabel(to)}
                  {!isPending && !isCancel && ' →'}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Cancel-reason confirm modal */}
      <CancelOrderModal
        open={cancelOpen}
        reason={cancelReason}
        onReasonChange={setCancelReason}
        onCancel={() => {
          setCancelOpen(false);
          setCancelReason('');
        }}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Header — navy gradient, mirrors QuoteHeader shape
// ─────────────────────────────────────────────────────────────

interface OrderHeaderProps {
  order: Order;
  customer: Customer | null;
  onCustomerClick: () => void;
}

function OrderHeader({ order, customer, onCustomerClick }: OrderHeaderProps) {
  return (
    <div className="bg-gradient-to-br from-navy to-[#2A3B52] text-white rounded shadow px-5 py-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold font-mono">{order.number}</h1>
            <OrderStatusPill status={order.status} size="md" />
          </div>

          {/* Customer line — clickable */}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {customer ? (
              <>
                <button
                  type="button"
                  onClick={onCustomerClick}
                  className="text-sm font-semibold hover:underline truncate text-left"
                  title={`Open ${customer.companyName}`}
                >
                  {customer.companyName}
                </button>
                <TierBadge tier={customer.tier} size="sm" />
                {customer.code && (
                  <span className="text-[11px] font-mono opacity-70">{customer.code}</span>
                )}
              </>
            ) : (
              <span className="text-sm opacity-70 italic">
                {order.customerNameSnapshot || 'No customer linked'}
              </span>
            )}
          </div>

          {/* Meta row */}
          <div className="text-[11px] opacity-85 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            {order.poNumber && (
              <>
                <span>
                  PO <span className="font-mono">{order.poNumber}</span>
                </span>
                <span className="opacity-60">·</span>
              </>
            )}
            {order.promisedShipDate && (
              <>
                <span title={fmtDate(order.promisedShipDate)}>
                  Promised {fmtRelativeAgo(order.promisedShipDate)}
                </span>
                <span className="opacity-60">·</span>
              </>
            )}
            {order.actualShipDate && (
              <>
                <span title={fmtDate(order.actualShipDate)}>
                  Shipped {fmtRelativeAgo(order.actualShipDate)}
                </span>
                <span className="opacity-60">·</span>
              </>
            )}
            <span title={new Date(order.updatedAt).toLocaleString()}>
              Updated {fmtRelativeAgo(order.updatedAt)}
            </span>
          </div>
        </div>

        {/* Right side: total snapshot */}
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wide opacity-70">Total</div>
          <div className="text-2xl font-bold font-mono">
            {!Number.isFinite(order.totalSnapshot) || order.totalSnapshot === 0 ? (
              <span className="opacity-50">—</span>
            ) : (
              fmtCurrency(order.totalSnapshot)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Details tab
// ─────────────────────────────────────────────────────────────

function DetailsTab({ order }: { order: Order }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Left column */}
      <div className="space-y-4">
        <PoInfoCard order={order} />
        <ShipInfoCard order={order} />
      </div>
      {/* Right column */}
      <div>
        <StatusTimelineCard order={order} />
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-paper border border-line rounded p-4">
      <h3 className="font-semibold text-navy mb-3">{title}</h3>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-0.5">
        {label}
      </div>
      <div className="text-text">{value}</div>
    </div>
  );
}

function PoInfoCard({ order }: { order: Order }) {
  return (
    <Card title="PO info">
      <div className="space-y-3">
        <FieldRow
          label="PO number"
          value={
            order.poNumber ? (
              <span className="font-mono">{order.poNumber}</span>
            ) : (
              <span className="text-text-mute italic">No PO recorded</span>
            )
          }
        />
        <FieldRow
          label="PO received"
          value={
            order.poReceivedAt ? (
              <span title={new Date(order.poReceivedAt).toLocaleString()}>
                {fmtDate(order.poReceivedAt)}{' '}
                <span className="text-text-mute text-xs">
                  ({fmtRelativeAgo(order.poReceivedAt)})
                </span>
              </span>
            ) : (
              <span className="text-text-mute italic">—</span>
            )
          }
        />
        {order.poDocumentId && (
          <FieldRow
            label="PO document"
            value={<span className="font-mono text-xs">{order.poDocumentId}</span>}
          />
        )}
      </div>
    </Card>
  );
}

function ShipInfoCard({ order }: { order: Order }) {
  return (
    <Card title="Ship info">
      <div className="space-y-3">
        <FieldRow
          label="Promised ship date"
          value={
            order.promisedShipDate ? (
              <span title={new Date(order.promisedShipDate).toLocaleString()}>
                {fmtDate(order.promisedShipDate)}{' '}
                <span className="text-text-mute text-xs">
                  ({fmtRelativeAgo(order.promisedShipDate)})
                </span>
              </span>
            ) : (
              <span className="text-text-mute italic">—</span>
            )
          }
        />
        <FieldRow
          label="Actual ship date"
          value={
            order.actualShipDate ? (
              <span title={new Date(order.actualShipDate).toLocaleString()}>
                {fmtDate(order.actualShipDate)}{' '}
                <span className="text-text-mute text-xs">
                  ({fmtRelativeAgo(order.actualShipDate)})
                </span>
              </span>
            ) : (
              <span className="text-text-mute italic">Not shipped yet</span>
            )
          }
        />
        {order.trackingNumber && (
          <FieldRow
            label="Tracking #"
            value={<span className="font-mono">{order.trackingNumber}</span>}
          />
        )}
      </div>
    </Card>
  );
}

function StatusTimelineCard({ order }: { order: Order }) {
  // Filter the audit trail down to status changes only — gives the operator
  // a clean visual history of the order's flow without the per-field update
  // noise. Newest first.
  const entries: AuditEntry[] = (order._audit ?? [])
    .filter((e) => e.action === 'status_change')
    .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());

  return (
    <Card title="Status timeline">
      {entries.length === 0 ? (
        <div className="text-text-mute italic text-sm">
          No status changes recorded yet — this order is still in its initial state.
        </div>
      ) : (
        <ol className="space-y-3">
          {entries.map((e, i) => {
            const change = e.fields?.status as
              | { from: unknown; to: unknown }
              | undefined;
            const fromLabel =
              change && typeof change.from === 'string'
                ? ORDER_STATUS_PILL[change.from as OrderStatus]?.label ?? String(change.from)
                : null;
            const toLabel =
              change && typeof change.to === 'string'
                ? ORDER_STATUS_PILL[change.to as OrderStatus]?.label ?? String(change.to)
                : null;
            const reason = (e.metadata as { reason?: string } | undefined)?.reason;
            return (
              <li key={`${e.ts}-${i}`} className="flex gap-3">
                <div className="shrink-0 w-2 h-2 rounded-full bg-orange mt-1.5" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-text">
                    {fromLabel && toLabel ? (
                      <span>
                        <span className="font-semibold">{fromLabel}</span>
                        <span className="text-text-mute mx-1">→</span>
                        <span className="font-semibold">{toLabel}</span>
                      </span>
                    ) : (
                      <span className="font-semibold">Status changed</span>
                    )}
                  </div>
                  {reason && (
                    <div className="text-xs text-text-mute mt-0.5 italic">"{reason}"</div>
                  )}
                  <div className="text-[11px] text-text-mute mt-0.5 flex flex-wrap items-center gap-2">
                    <time dateTime={e.ts} className="font-mono">
                      {new Date(e.ts).toLocaleString()}
                    </time>
                    <span className="opacity-60">·</span>
                    <span>{e.user}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Parts tab — read-only list, pulled from the source quote
// ─────────────────────────────────────────────────────────────

function PartsTab({
  quote,
  partsById,
}: {
  quote: Quote | null;
  partsById: Record<string, Part>;
}) {
  const navigate = useNavigate();

  if (!quote) {
    return (
      <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
        <div className="text-sm">No source quote linked — parts list unavailable.</div>
        <div className="text-[11px] mt-1 italic">
          Orders converted from a quote carry their parts via{' '}
          <span className="font-mono">fromQuoteId</span>.
        </div>
      </div>
    );
  }

  const partIds = quote.partIds ?? [];
  if (partIds.length === 0) {
    return (
      <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
        <div className="text-sm">No parts on the source quote.</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-text-mute mb-1">
        {partIds.length} part{partIds.length === 1 ? '' : 's'} from quote{' '}
        <span className="font-mono">{quote.number}</span>
      </div>
      {partIds.map((pid) => {
        const part = partsById[pid];
        if (!part) {
          return (
            <div
              key={pid}
              className="border border-line rounded p-3 bg-bg/40 text-sm text-text-mute italic"
            >
              Part record missing (may have been soft-deleted). ID{' '}
              <span className="font-mono">{pid}</span>
            </div>
          );
        }
        return (
          <button
            key={pid}
            type="button"
            onClick={() => navigate(`/parts/${pid}`)}
            className="w-full text-left border border-line rounded p-3 hover:border-orange hover:shadow-sm transition-all bg-paper flex items-center gap-3"
          >
            <div className="shrink-0">
              <PartCardImage part={part} width={72} staticVisual />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono font-bold text-navy truncate">{part.number}</div>
              <div className="text-xs text-text-mute mt-0.5 truncate" title={part.description}>
                {part.description}
              </div>
            </div>
            <span className="text-xs text-orange shrink-0">Open →</span>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Activity tab — full _audit[] timeline
// ─────────────────────────────────────────────────────────────

function ActivityTab({ order }: { order: Order }) {
  const entries: AuditEntry[] = order._audit ?? [];

  if (entries.length === 0) {
    return (
      <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
        <div className="text-sm">No activity yet.</div>
        <div className="text-[11px] mt-1 italic">
          Audit entries populate as the order is updated and moves through statuses.
        </div>
      </div>
    );
  }

  const sorted = [...entries].sort(
    (a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime(),
  );

  return (
    <div className="bg-paper border border-line rounded p-4">
      <ol className="space-y-3">
        {sorted.map((e, i) => (
          <li key={`${e.ts}-${i}`} className="flex gap-3">
            <div className="shrink-0 w-2 h-2 rounded-full bg-orange mt-1.5" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-text">
                <span className="font-semibold capitalize">{e.action.replace(/_/g, ' ')}</span>
                {e.fields && Object.keys(e.fields).length > 0 && (
                  <span className="text-text-mute"> · {Object.keys(e.fields).join(', ')}</span>
                )}
              </div>
              <div className="text-[11px] text-text-mute mt-0.5 flex flex-wrap items-center gap-2">
                <time dateTime={e.ts} className="font-mono">
                  {new Date(e.ts).toLocaleString()}
                </time>
                <span className="opacity-60">·</span>
                <span>{e.user}</span>
                <span className="opacity-60">·</span>
                <span className="italic">{e.source}</span>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Cancel-order confirm modal — captures the optional reason
// ─────────────────────────────────────────────────────────────

interface CancelOrderModalProps {
  open: boolean;
  reason: string;
  onReasonChange: (next: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

function CancelOrderModal({
  open,
  reason,
  onReasonChange,
  onCancel,
  onConfirm,
}: CancelOrderModalProps) {
  // Escape closes the modal — mirrors the contract used by the other modals.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-order-title"
    >
      <div
        className="bg-paper rounded shadow-lg w-full max-w-[440px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-line bg-navy text-paper">
          <h2 id="cancel-order-title" className="font-bold text-base">
            Cancel this order?
          </h2>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-text">
            Cancelling moves this order to <span className="font-semibold">void</span>. This is a
            terminal status — it can't be un-cancelled later.
          </p>
          <label className="block">
            <span className="block text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
              Reason (optional)
            </span>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Customer pulled PO / duplicate / etc."
              rows={3}
              autoFocus
              className="w-full px-3 py-2 border border-line-dark rounded text-sm focus:outline-none focus:border-orange resize-y"
            />
          </label>
        </div>
        <div className="px-4 pb-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg"
          >
            Keep order
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
          >
            Cancel order
          </button>
        </div>
      </div>
    </div>
  );
}
